import React, { useEffect, useState } from "react";
import { Box, Button, Card, CircularProgress, Divider, Tab, Tabs, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import moment from "moment";

// API functions
import getDynamicFormData from "api/llm/getDynamicFormData";
import getAllForms from "api/llm/getAllForms";
import updateDynamicForm from "api/llm/updateDynamicForm";
import generateFilledPdf from "api/llm/generateFilledPdf";
import deleteDynamicForm from "api/llm/deleteDynamicForm";

// Components
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import ConfirmModal from "components/ConfirmDialog/ConfirmModal";
import FormRenderer from "./components/FormRenderer";
import FormsList from "./components/FormsList";
import FormUploadModal from "./components/FormUploadModal";
import FormEditor from "./components/FormEditor";
import FormMetadataEditorDialog from "./components/FormMetadataEditorDialog";
import OverlayEditorModal from "./OpenSea/OverlayEditorModal";

const DynamicForms = () => {
  // State management
  const [formData, setFormData] = useState(null);
  const [currentFormId, setCurrentFormId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formsList, setFormsList] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [formName, setFormName] = useState("");
  const [uploadedForm, setUploadedForm] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [formValues, setFormValues] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [visibility, setVisibility] = useState("Both");
  const [onboarding, setOnboarding] = useState("No");
  const [minOnboardingStage, setMinOnboardingStage] = useState("New");
  const [required, setRequired] = useState("No");
  const [shortName, setShortName] = useState("");
  const [status, setStatus] = useState("Active");
  const [formMetadata, setFormMetadata] = useState({
    visibility: "Both",
    onboarding: "No",
    minOnboardingStage: "New",
    required: "No",
    shortName: "",
    status: "Active",
  });
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [tempMetadata, setTempMetadata] = useState({
    visibility: "Both",
    onboarding: "No",
    minOnboardingStage: "New",
    required: "No",
    shortName: "",
    status: "Active",
  });
  const [showOverlayModal, setShowOverlayModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [tempFormName, setTempFormName] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [deletingForm, setDeletingForm] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  // Fetch all forms with complete data on component mount
  const fetchForms = async () => {
    try {
      setLoadingForms(true);
      const forms = await getAllForms({ outsideMode: "" });
      setFormsList(forms);
    } catch (error) {
      enqueueSnackbar(error.message || "Error fetching forms", {
        variant: "error",
      });
    } finally {
      setLoadingForms(false);
    }
  };

  useEffect(() => {
    fetchForms();
  }, []);

  // Event handlers
  const handleFileUpload = async (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      enqueueSnackbar(rejectedFiles[0].errors[0].message, {
        variant: "error",
        autoHideDuration: 3000,
      });
      return;
    }
    if (acceptedFiles.length <= 0) return;

    // Create form data
    const formUpload = new FormData();
    formUpload.append("file", acceptedFiles[0]);

    // Store the form data for later use
    setUploadedForm(formUpload);
  };

  const handleProcessForm = async () => {
    if (!uploadedForm) return;

    try {
      setLoading(true);

      const response = await getDynamicFormData({
        form: uploadedForm,
        formName,
        metadata: {
          visibility,
          onboarding,
          minOnboardingStage: onboarding === "Yes" ? minOnboardingStage : null,
          required: onboarding === "Yes" ? required : "No",
          shortName,
          status,
        },
      });

      setFormData(response.data.form);
      setCurrentFormId(response.data.formId);
      setFormValues({});

      // Set form metadata from response
      if (response.data.metadata) {
        setFormMetadata(response.data.metadata);
      }

      // Refresh forms list
      fetchForms();

      // Switch to form view tab
      setActiveTab(1);

      // Automatically open the overlay editor after successful upload
      setSelectedForm({
        id: response.data.formId,
        form: response.data.form,
        metadata: response.data.metadata || {},
        ...response.data,
      });
      setShowOverlayModal(true);

      enqueueSnackbar("Form processed and saved successfully", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(error.message || "Error processing form", {
        variant: "error",
      });
    } finally {
      setLoading(false);
      setUploadedForm(null);
      setFormName("");
      // Ensure all form upload states are reset
      setShortName("");
      setStatus("Active");
      setOnboarding("No");
      setMinOnboardingStage("New");
      setRequired("No");
      setVisibility("Both");
    }
  };

  const handleCloseUploadModal = async (shouldProcess) => {
    setShowUploadModal(false);

    if (shouldProcess && uploadedForm) {
      await handleProcessForm();
    }
  };

  const handleLoadForm = (formId) => {
    try {
      // Find the form in our already loaded forms list
      const formToLoad = formsList.find((form) => form.id === formId);

      if (formToLoad && formToLoad.form) {
        setFormData(formToLoad.form);
        setCurrentFormId(formToLoad.id);
        setFormValues({});
        setSelectedForm(formToLoad);

        // Set form metadatacc
        if (formToLoad.metadata) {
          setFormMetadata(formToLoad.metadata);
        }
        setActiveTab(1); // Switch to form view tab
        setIsEditMode(false); // Reset edit mode
      } else {
        enqueueSnackbar("Form data not found", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(error.message || "Error loading form", {
        variant: "error",
      });
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset edit mode when changing tabs
    if (newValue !== 2) {
      setIsEditMode(false);
    }
  };

  const handleInputChange = (id, value) => {
    setFormValues((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form values:", formValues);
    enqueueSnackbar("Form submitted successfully", {
      variant: "success",
    });
    // TODO: Handle form submission
  };

  const handleEditForm = () => {
    setIsEditMode(true);
    setActiveTab(2); // Switch to edit tab
  };

  const handleFormDataChange = async (updatedFormData) => {
    try {
      setLoading(true);

      if (!currentFormId) {
        throw new Error("Form ID not found");
      }

      // Update the form data and preserve existing metadata
      await updateDynamicForm({
        formId: currentFormId,
        formData: updatedFormData,
      });

      // Update local state
      setFormData(updatedFormData);

      // Refresh forms list
      await fetchForms();

      // Return to form view
      setIsEditMode(false);
      setActiveTab(1);

      enqueueSnackbar("Form updated successfully", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(error.message || "Error updating form", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePdf = async (values) => {
    if (!currentFormId) {
      enqueueSnackbar("Form ID not found", { variant: "error" });
      return;
    }

    try {
      setGeneratingPdf(true);

      const response = await generateFilledPdf({
        formId: currentFormId,
        formValues: values,
      });

      // Create a blob URL for the PDF
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Open the PDF in a new tab
      window.open(url, "_blank");

      enqueueSnackbar("PDF generated and opened successfully", { variant: "success" });
    } catch (error) {
      console.error("Error generating PDF:", error);
      enqueueSnackbar(error.message || "Error generating PDF", { variant: "error" });
    } finally {
      setGeneratingPdf(false);
    }
  };
  // New function to handle temp metadata changes
  const handleTempMetadataChange = (newMetadata) => {
    setTempMetadata(newMetadata);
  };

  // New function to handle temp form name changes
  const handleTempFormNameChange = (newFormName) => {
    setTempFormName(newFormName);
  };

  // New function to save both form name and metadata
  const handleSaveMetadata = async () => {
    try {
      setUpdatingMetadata(true);

      if (!currentFormId) {
        throw new Error("Form ID not found");
      }

      // Update both form name and metadata
      await updateDynamicForm({
        formId: currentFormId,
        formData,
        metadata: tempMetadata,
        formName: tempFormName,
      });

      // Update local state
      setFormMetadata(tempMetadata);

      // Close the modal
      setShowMetadataModal(false);

      enqueueSnackbar("Form metadata updated successfully", {
        variant: "success",
      });

      // Refresh forms list
      await fetchForms();
    } catch (error) {
      enqueueSnackbar(error.message || "Error updating form metadata", {
        variant: "error",
      });
    } finally {
      setUpdatingMetadata(false);
    }
  };

  // New function to handle metadata editing from forms list
  const handleEditMetadataFromList = (formId, existingMetadata, formNameToEdit) => {
    setCurrentFormId(formId);
    // Set temp metadata for editing
    if (existingMetadata) {
      setTempMetadata({ ...existingMetadata });
    }
    // Set temp form name for editing
    setTempFormName(formNameToEdit || "");
    // Open the metadata modal
    setShowMetadataModal(true);
  };

  // Handle opening the overlays editor modal
  const handleOpenOverlaysEditor = () => {
    if (!selectedForm) {
      enqueueSnackbar("No form selected", { variant: "error" });
      return;
    }
    setShowOverlayModal(true);
  };

  const handleUploadClick = () => {
    // Reset form state
    setUploadedForm(null);
    setShortName("");
    setStatus("Active");
    setOnboarding("No");
    setMinOnboardingStage("New");
    setRequired("No");
    setVisibility("Both");
    // Use US format for date (MM-DD-YYYY)
    setFormName(`Form ${moment().format("MM-DD-YYYY")}`);
    setShowUploadModal(true);
  };

  // New function to handle form deletion request
  const handleDeleteFormRequest = (formId, deleteFormName, deleteShortName) => {
    setFormToDelete({ formId, formName: deleteFormName, shortName: deleteShortName });
    setShowDeleteConfirmation(true);
  };

  // New function to confirm form deletion
  const handleConfirmDeleteForm = async () => {
    if (!formToDelete) return;

    try {
      setDeletingForm(true);

      // Call the delete API
      await deleteDynamicForm(formToDelete.formId);

      // If this was the currently selected form, clear it
      if (currentFormId === formToDelete.formId) {
        setFormData(null);
        setCurrentFormId(null);
        setSelectedForm(null);
        setActiveTab(0); // Switch back to forms list
      }

      // Close confirmation modal
      setShowDeleteConfirmation(false);
      setFormToDelete(null);

      enqueueSnackbar("Form deleted successfully", {
        variant: "success",
      });
      // Refresh forms list
      await fetchForms();
    } catch (error) {
      enqueueSnackbar(error.message || "Error deleting form", {
        variant: "error",
      });
    } finally {
      setDeletingForm(false);
    }
  };

  return (
    <Card sx={{ width: "100%", p: 3, position: "relative" }}>
      {/* Header with title and add button */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">Dynamic Forms</Typography>
        <Button
          startIcon={<AddCircleRoundedIcon />}
          variant="text"
          onClick={handleUploadClick}
          aria-label="add form"
        >
          Add New Form
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Forms Library" />
        {formData && <Tab label="Form View" />}
        {formData && isEditMode && <Tab label="Form Editor" />}
      </Tabs>

      {/* Loading indicator */}
      {(loading || generatingPdf) && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", my: 4 }}>
          <CircularProgress />
          {loading && (
            <Typography variant="body1" sx={{ mt: 2, textAlign: "center", maxWidth: "500px" }}>
              Processing your form. This might take a few minutes depending on the number of pages.
              Please don&apos;t close this window.
            </Typography>
          )}
          {generatingPdf && (
            <Typography variant="body1" sx={{ mt: 2 }}>
              Generating PDF...
            </Typography>
          )}
        </Box>
      )}

      {/* Forms Library Tab */}
      {!loading && !generatingPdf && activeTab === 0 && (
        <FormsList
          forms={formsList}
          loading={loadingForms}
          onViewForm={handleLoadForm}
          onEditMetadata={handleEditMetadataFromList}
          onDeleteForm={handleDeleteFormRequest}
        />
      )}

      {/* Form View Tab */}
      {!loading && !generatingPdf && activeTab === 1 && formData && (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => handleGeneratePdf(formValues)}
              disabled={generatingPdf}
            >
              Generate PDF
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={<BorderColorIcon />}
              onClick={handleOpenOverlaysEditor}
            >
              Edit Overlays
            </Button>
            <Button variant="contained" color="primary" onClick={handleEditForm}>
              Edit Form
            </Button>
          </Box>
          <FormRenderer
            formData={formData}
            formValues={formValues}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
          />
        </>
      )}

      {/* Form Editor Tab */}
      {activeTab === 2 && formData && isEditMode && (
        <FormEditor formData={formData} onFormDataChange={handleFormDataChange} loading={loading} />
      )}

      {/* Modals */}
      {showUploadModal && (
        <FormUploadModal
          open={showUploadModal}
          onClose={handleCloseUploadModal}
          onUpload={handleFileUpload}
          loading={loading}
          formName={formName}
          setFormName={setFormName}
          visibility={visibility}
          setVisibility={setVisibility}
          onboarding={onboarding}
          setOnboarding={setOnboarding}
          minOnboardingStage={minOnboardingStage}
          setMinOnboardingStage={setMinOnboardingStage}
          required={required}
          setRequired={setRequired}
          shortName={shortName}
          setShortName={setShortName}
          status={status}
          setStatus={setStatus}
        />
      )}

      {/* Metadata Edit Modal */}
      <FormMetadataEditorDialog
        open={showMetadataModal}
        onClose={() => setShowMetadataModal(false)}
        metadata={tempMetadata}
        onMetadataChange={handleTempMetadataChange}
        formName={tempFormName}
        onFormNameChange={handleTempFormNameChange}
        onSave={handleSaveMetadata}
        loading={updatingMetadata}
      />

      {/* Overlays Editor Modal */}
      <OverlayEditorModal
        open={showOverlayModal}
        setOpen={setShowOverlayModal}
        selectedForm={selectedForm}
        onSave={(updatedFormData) => {
          // Update the form data in state
          setFormData(updatedFormData);

          // Call updateDynamicForm API to save the changes
          updateDynamicForm({
            formId: currentFormId,
            formData: updatedFormData,
          })
            .then(() => {
              // Show success message
              enqueueSnackbar("Form overlay positions updated successfully", {
                variant: "success",
              });

              // Update the selectedForm state with the new overlay positions
              setSelectedForm((prevSelectedForm) => ({
                ...prevSelectedForm,
                form: updatedFormData,
              }));

              // Refresh the forms list
              fetchForms();
            })
            .catch((error) => {
              enqueueSnackbar(error.message || "Error updating form overlay positions", {
                variant: "error",
              });
            });
        }}
      />

      {/* Delete Form Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirmation}
        closeFn={() => {
          if (!deletingForm) {
            setShowDeleteConfirmation(false);
            setFormToDelete(null);
          }
        }}
        title={`Delete Form: ${formToDelete?.formName || "Unknown Form"}`}
        description={
          formToDelete ? (
            <Box sx={{ py: 1 }}>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                Are you sure you want to delete the form:
              </Typography>

              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="h6" sx={{ mb: 1, color: "#d32f2f", fontWeight: 600 }}>
                  {formToDelete.formName}
                </Typography>
                {formToDelete.shortName && (
                  <Typography variant="body2" sx={{ color: "#666", fontStyle: "italic" }}>
                    Short name: {formToDelete.shortName}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#d32f2f",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  ⚠️ This action cannot be undone
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#1976d2",
                    backgroundColor: "#e3f2fd",
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid #bbdefb",
                  }}
                >
                  <strong>Note:</strong> Deletion will be prevented if any applicants have already
                  submitted this form.
                </Typography>
              </Box>
            </Box>
          ) : (
            ""
          )
        }
        negativeBtn={{
          label: "Cancel",
          fn: () => {
            if (!deletingForm) {
              setShowDeleteConfirmation(false);
              setFormToDelete(null);
            }
          },
          isShown: true,
        }}
        positiveBtn={{
          label: deletingForm ? "Deleting..." : "Delete Form",
          fn: deletingForm ? null : handleConfirmDeleteForm,
          isShown: true,
        }}
      />
    </Card>
  );
};

export default DynamicForms;
